import { useState } from "react";
import { ChevronDown, Target, Ban, ListChecks, ShieldCheck, ShieldAlert } from "lucide-react";
import { Panel, Tag, Stat } from "@/components/common";
import { fmtMoney } from "@/lib/api";
import RsaEditor, { LIMITS } from "@/components/plan/RsaEditor";
import NegativesEditor from "@/components/NegativesEditor";

const fmtKw = (k) => (k.match_type === "EXACT" ? `[${k.text}]` : k.match_type === "PHRASE" ? `"${k.text}"` : k.text);
const MATCH_TONE = { EXACT: "emerald", PHRASE: "cyan", BROAD: "amber" };
const OUTDATED = /existing (live )?campaign|keeps? running|runs? separately|50\s*\/\s*50|50-50|split (the )?budget|budget split|even(ly)? split/i;
const MONEY = /S\$\s?([\d,]+(?:\.\d+)?)\s*(?:\/|per )\s*(?:day|month)/gi;

const outdated = (s, live) => {
  if (OUTDATED.test(s)) return true;
  const ok = new Set([live.total_daily, live.total_monthly, ...live.campaigns.flatMap((c) => [c.daily_budget, c.monthly_budget])].filter((v) => v != null).map(Number));
  return [...s.matchAll(MONEY)].some((m) => !ok.has(Number(m[1].replace(/,/g, ""))));
};

export const Narrative = ({ text, live, testid }) => {
  if (!live || !text) return <>{text}</>;
  const parts = text.match(/[^.!?]+[.!?]*\s*/g) || [text];
  return <>{parts.map((s, i) => (outdated(s, live)
    ? <span key={i} data-testid={testid ? `${testid}-outdated-${i}` : undefined}><span className="text-slate-500 line-through">{s}</span><span className="mx-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-medium text-amber-300 no-underline">Outdated · now: {live.banner}</span> </span>
    : <span key={i}>{s}</span>))}</>;
};

const Budget = ({ label, live, planned, currency, testid }) => (
  <div className="text-right" data-testid={testid}><div className="cc-eyebrow">{live != null ? `Live ${label}` : label}</div>
    <div className="font-mono text-lg font-bold text-emerald-300">{fmtMoney(live ?? planned, currency)}</div>
    {live != null && <div className="font-mono text-[10px] text-slate-500">Original plan {fmtMoney(planned, currency)}</div>}</div>
);

export const countViolations = (plan) => {
  let n = 0;
  (plan.campaigns || []).forEach((c) => (c.ad_groups || []).forEach((g) => (g.responsive_search_ads || []).forEach((ad) => {
    n += (ad.headlines || []).filter((h) => h.length > LIMITS.headline).length;
    n += (ad.descriptions || []).filter((d) => d.length > LIMITS.description).length;
    n += ["path1", "path2"].filter((p) => (ad[p] || "").length > LIMITS.path).length;
  })));
  return n;
};

const Negatives = ({ items, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {(items || []).map((n, i) => <span key={i} className="rounded border border-red-500/20 bg-red-500/5 px-2 py-0.5 font-mono text-[11px] text-red-300">−{fmtKw(n)}</span>)}
    {!(items || []).length && <span className="text-xs text-slate-500">None</span>}
  </div>
);

const AdGroup = ({ g, ci, gi, onAd, domain }) => (
  <div className="rounded-md border border-[#1e293b] p-5" data-testid={`plan-adgroup-${ci}-${gi}`}>
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <div className="font-display text-xl font-bold uppercase tracking-tight text-slate-100">{g.name}</div>
      <span className="truncate font-mono text-[11px] text-slate-500">{g.final_url}</span>
    </div>
    {g.theme && <p className="mt-1 text-sm text-slate-400">{g.theme}</p>}
    <div className="cc-eyebrow mb-2 mt-5">Keywords ({(g.keywords || []).length})</div>
    <div className="flex flex-wrap gap-1.5" data-testid={`plan-keywords-${ci}-${gi}`}>
      {(g.keywords || []).map((k, i) => <Tag key={i} tone={MATCH_TONE[k.match_type] || "muted"} className="normal-case tracking-normal">{fmtKw(k)}</Tag>)}
    </div>
    {(g.responsive_search_ads || []).map((ad, ai) => (
      <div key={ai} className="mt-6"><div className="cc-eyebrow mb-3">Responsive search ad {ai + 1}</div>
        <RsaEditor ad={ad} domain={domain} testid={`rsa-${ci}-${gi}-${ai}`} onChange={(next) => onAd(ai, next)} /></div>
    ))}
  </div>
);

const Campaign = ({ c, ci, currency, onChange, domain, live }) => {
  const [open, setOpen] = useState(ci === 0);
  const lc = live?.campaigns.find((x) => x.name === c.name);
  const setAd = (gi, ai, ad) => onChange({ ...c, ad_groups: c.ad_groups.map((g, j) => (j !== gi ? g : { ...g, responsive_search_ads: g.responsive_search_ads.map((a, k) => (k === ai ? ad : a)) })) });
  return (
    <section className="cc-card am-rise" data-testid={`plan-campaign-${ci}`}>
      <button data-testid={`plan-campaign-toggle-${ci}`} onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center justify-between gap-4 p-6 text-left">
        <div>
          <div className="cc-eyebrow text-emerald-400/80">Campaign {ci + 1} · {c.campaign_type}</div>
          <div className="mt-1 font-display text-2xl font-bold uppercase tracking-tight text-slate-50 md:text-3xl">{c.name}</div>
          <div className="mt-1 text-sm text-slate-400">{c.objective}</div>
        </div>
        <div className="flex items-center gap-6">
          <Budget label="Daily" live={lc?.daily_budget} planned={c.daily_budget} currency={currency} testid={`plan-campaign-daily-${ci}`} />
          <Budget label="Monthly" live={lc?.daily_budget != null ? lc.monthly_budget : null} planned={c.monthly_budget} currency={currency} testid={`plan-campaign-monthly-${ci}`} />
          <ChevronDown className={`h-5 w-5 text-slate-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="space-y-6 border-t border-[#1e293b] p-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-md bg-[#0a101d] p-4"><div className="cc-eyebrow">Bidding</div><div className="mt-1 font-mono text-sm text-slate-100" data-testid={`plan-bidding-${ci}`}>{c.bidding_strategy?.type}{c.bidding_strategy?.target_cpa ? ` · tCPA ${fmtMoney(c.bidding_strategy.target_cpa, currency)}` : ""}</div><p className="mt-1 text-xs text-slate-500"><Narrative text={c.bidding_strategy?.rationale} live={live} /></p></div>
            <div className="rounded-md bg-[#0a101d] p-4"><div className="cc-eyebrow">Locations · languages</div><div className="mt-1 text-sm text-slate-200">{(c.locations || []).join(", ")}</div><div className="text-xs text-slate-500">{(c.languages || []).join(", ")}</div></div>
            <div className="rounded-md bg-[#0a101d] p-4"><div className="cc-eyebrow">{live?.ad_schedule ? "Live schedule (SGT)" : "Schedule"}</div><div className="mt-1 text-sm text-slate-200" data-testid={`plan-schedule-${ci}`}>{live?.ad_schedule || c.ad_schedule || "—"}</div>
              {live?.ad_schedule && c.ad_schedule && <div className="mt-1 text-[10px] text-slate-500">Original plan: {c.ad_schedule}</div>}</div>
          </div>
          <div><div className="cc-eyebrow mb-2">Campaign negative keywords</div><NegativesEditor items={c.negative_keywords || []} onChange={(v) => onChange({ ...c, negative_keywords: v })} testid={`plan-negatives-${ci}`} /></div>
          {(c.ad_groups || []).map((g, gi) => <AdGroup key={gi} g={g} ci={ci} gi={gi} domain={domain} onAd={(ai, ad) => setAd(gi, ai, ad)} />)}
        </div>
      )}
    </section>
  );
};

export default function PlanView({ plan, validation, onChange, domain, live }) {
  const liveViolations = countViolations(plan);
  const cur = plan.currency || "SGD";
  const adGroups = (plan.campaigns || []).reduce((n, c) => n + (c.ad_groups || []).length, 0);
  const keywords = (plan.campaigns || []).reduce((n, c) => n + (c.ad_groups || []).reduce((m, g) => m + (g.keywords || []).length, 0), 0);
  return (
    <div className="space-y-6" data-testid="plan-view">
      <div className={`flex items-start gap-3 rounded-md border p-4 text-sm ${liveViolations ? "border-red-500/30 bg-red-500/10 text-red-200" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"}`} data-testid="plan-validation-banner">
        {liveViolations ? <ShieldAlert className="h-5 w-5 shrink-0" /> : <ShieldCheck className="h-5 w-5 shrink-0" />}
        <div>
          <div className="font-semibold">{liveViolations ? `${liveViolations} ad text item(s) exceed Google's limits` : "All ad copy is within Google's limits"}</div>
          <div className="mt-0.5 font-mono text-xs opacity-80">Headlines ≤30 · descriptions ≤90 · paths ≤15 · max 15 headlines / 4 descriptions{validation ? ` · auto-fixed ${validation.repaired} · removed ${validation.removed}` : ""}</div>
          {validation?.count_issues?.length > 0 && <ul className="mt-1 text-xs opacity-80">{validation.count_issues.map((x) => <li key={x}>{x}</li>)}</ul>}
        </div>
      </div>
      <Panel title="Strategy" icon={Target} testid="plan-strategy">
        <p className="text-base leading-relaxed text-slate-200" data-testid="plan-strategy-text"><Narrative text={plan.strategy_summary} live={live} testid="plan-strategy" /></p>
      </Panel>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div data-testid="plan-total-budget-wrap"><Stat label={live ? "Live monthly budget" : "Monthly budget"} value={fmtMoney(live ? live.total_monthly : plan.total_monthly_budget, cur)} testid="plan-total-budget" />
          {live && <div className="mt-1 font-mono text-[10px] text-slate-500" data-testid="plan-original-budget">Original plan {fmtMoney(plan.total_monthly_budget, cur)}/month</div>}</div>
        <Stat label="Campaigns" value={(plan.campaigns || []).length} testid="plan-campaign-count" />
        <Stat label="Ad groups" value={adGroups} testid="plan-adgroup-count" />
        <Stat label="Keywords" value={keywords} testid="plan-keyword-count" />
      </div>
      {(plan.campaigns || []).map((c, ci) => (
        <Campaign key={ci} c={c} ci={ci} currency={cur} domain={domain} live={live} onChange={(next) => onChange({ ...plan, campaigns: plan.campaigns.map((x, j) => (j === ci ? next : x)) })} />
      ))}
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Account negative keywords" icon={Ban} testid="plan-account-negatives"><NegativesEditor items={plan.account_negative_keywords || []} onChange={(v) => onChange({ ...plan, account_negative_keywords: v })} testid="plan-account-negatives-list" /></Panel>
        <Panel title="Next steps" icon={ListChecks} testid="plan-next-steps">
          <ol className="space-y-2">{(plan.next_steps || []).map((s, i) => <li key={i} className="flex gap-3 text-sm text-slate-300"><span className="font-mono text-xs text-emerald-400">{String(i + 1).padStart(2, "0")}</span><span><Narrative text={s} live={live} /></span></li>)}</ol>
        </Panel>
      </div>
    </div>
  );
}
